import React, { useState } from "react";
import { Modal, notification } from "antd";
import { Field, reduxForm } from "redux-form";
import validator from "validator";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material";

import { customInput } from "../../../../common/custom-input";
import v2Styles from "../../../v2Styles.module.css";
import jobStyles from "../../../Job/JobStyle.module.css";
import { Strings } from "../../../../../dataProvider/localize";

const validate = (values) => {
  const errors = {};
  const isDigitsOnly = (value) => /^\d+$/.test(value);

  if (
    values.email_address &&
    values.email_address.trim() !== "" &&
    !validator.isEmail(values.email_address.trim())
  ) {
    errors.email_address = "Invalid email";
  }

  if (
    values.client_person_email &&
    values.client_person_email.trim() !== "" &&
    !validator.isEmail(values.client_person_email.trim())
  ) {
    errors.client_person_email = "Invalid email";
  }

  if (
    values.phone_number &&
    values.phone_number.trim() !== "" &&
    !isDigitsOnly(values.phone_number.trim())
  ) {
    errors.phone_number = "Phone number must contain numbers only";
  }

  if (
    values.client_person_phone &&
    values.client_person_phone.trim() !== "" &&
    !isDigitsOnly(values.client_person_phone.trim())
  ) {
    errors.client_person_phone = "Phone number must contain numbers only";
  }

  return errors;
};

const EditDocumentInfoForm = ({ handleSubmit, onClose, saving }) => {
  return (
    <form onSubmit={handleSubmit}>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className={v2Styles.ModalHeading}>Document Info</div>
            <div>
              <hr className="v3-modal-hr" />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-md-6 pb-2">
            <div className={`${jobStyles.SectionHeading} mb-2`}>By</div>

            <div className="pb-2">
              <div className={v2Styles.Label}>Prepared By</div>
              <Field
                name="first_name"
                type="text"
                disabled={saving}
                autoComplete="off"
                className={v2Styles.TextField}
                component={customInput}
              />
            </div>

            <div className="pb-2">
              <div className={v2Styles.Label}>Company</div>
              <Field
                name="org_name"
                type="text"
                disabled={saving}
                autoComplete="off"
                className={v2Styles.TextField}
                component={customInput}
              />
            </div>

            <div className="pb-2">
              <div className={v2Styles.Label}>Address</div>
              <Field
                name="address"
                type="text"
                disabled={saving}
                autoComplete="off"
                className={v2Styles.TextField}
                component={customInput}
              />
            </div>

            <div className="pb-2">
              <div className={v2Styles.Label}>Mobile</div>
              <Field
                name="phone_number"
                type="text"
                disabled={saving}
                autoComplete="off"
                className={v2Styles.TextField}
                component={customInput}
              />
            </div>

            <div className="pb-2">
              <div className={v2Styles.Label}>Email</div>
              <Field
                name="email_address"
                type="text"
                disabled={saving}
                autoComplete="off"
                className={v2Styles.TextField}
                component={customInput}
              />
            </div>
          </div>

          <div className="col-12 col-md-6 pb-2">
            <div className={`${jobStyles.SectionHeading} mb-2`}>To</div>

            <div className="pb-2">
              <div className={v2Styles.Label}>Attention</div>
              <Field
                name="client_person_name"
                type="text"
                disabled={saving}
                autoComplete="off"
                className={v2Styles.TextField}
                component={customInput}
              />
            </div>

            <div className="pb-2">
              <div className={v2Styles.Label}>Company</div>
              <Field
                name="client_name"
                type="text"
                disabled={saving}
                autoComplete="off"
                className={v2Styles.TextField}
                component={customInput}
              />
            </div>

            <div className="pb-2">
              <div className={v2Styles.Label}>Address</div>
              <Field
                name="client_address"
                type="text"
                disabled={saving}
                autoComplete="off"
                className={v2Styles.TextField}
                component={customInput}
              />
            </div>

            <div className="pb-2">
              <div className={v2Styles.Label}>Mobile</div>
              <Field
                name="client_person_phone"
                type="text"
                disabled={saving}
                autoComplete="off"
                className={v2Styles.TextField}
                component={customInput}
              />
            </div>

            <div className="pb-2">
              <div className={v2Styles.Label}>Email</div>
              <Field
                name="client_person_email"
                type="text"
                disabled={saving}
                autoComplete="off"
                className={v2Styles.TextField}
                component={customInput}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="d-flex align-items-center justify-content-end" style={{ gap: "5px" }}>
              {saving ? <CircularProgress size={20} /> : ""}
              <button type="button" disabled={saving} className={v2Styles.OutlinedButton} onClick={onClose}>
                Cancel
              </button>
              <button type="submit" disabled={saving} className={v2Styles.PrimaryButton}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

const ReduxEditDocumentInfoForm = reduxForm({
  form: "postAllocateDocumentInfoEdit",
  validate,
  enableReinitialize: true,
})(EditDocumentInfoForm);

const EditDocumentInfoModal = ({ visible, onClose, initialValues, onSave, onSaveApi, jobId, jobDocumentId }) => {
  const [saving, setSaving] = useState(false);

  const getPayload = (values) => ({
    job_doc_info: {
      by: {
        first_name: values.first_name ? values.first_name : "",
        org_name: values.org_name ? values.org_name : "",
        address: values.address ? values.address : "",
        phone_number: values.phone_number ? values.phone_number : "",
        email_address: values.email_address ? values.email_address : "",
      },
      to: {
        client_person_name: values.client_person_name ? values.client_person_name : "",
        client_name: values.client_name ? values.client_name : "",
        client_address: values.client_address ? values.client_address : "",
        client_person_phone: values.client_person_phone ? values.client_person_phone : "",
        client_person_email: values.client_person_email ? values.client_person_email : "",
      },
    },
  });

  const handleSubmit = async (values) => {
    if (!onSaveApi || !jobId || !jobDocumentId) {
      notification.error({
        message: Strings.error_title,
        description: Strings.generic_error,
        className: "ant-error",
      });
      return;
    }

    setSaving(true);
    try {
      const response = await onSaveApi(jobId, jobDocumentId, getPayload(values));

      notification.success({
        message: Strings.success_title,
        description: response && response.message ? response.message : Strings.success_title,
        className: "ant-success",
      });

      if (onSave) {
        await onSave();
      }
      onClose();
    } catch (message) {
      const errorMessage = Array.isArray(message)
        ? message[0] && message[0].message ? message[0].message : Strings.generic_error
        : message;

      notification.error({
        message: Strings.error_title,
        description: errorMessage ? errorMessage : Strings.generic_error,
        className: "ant-error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      className="cs-modal-v2"
      visible={visible}
      centered
      width="800px"
      footer={null}
      destroyOnClose={true}
      maskClosable={false}
      bodyStyle={{ paddingRight: "0px", paddingLeft: "0px" }}
      onCancel={onClose}
      closeIcon={<div className={jobStyles.ModalCloseIcon}><CloseIcon /></div>}
    >
      <ReduxEditDocumentInfoForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        saving={saving}
      />
    </Modal>
  );
};

export default EditDocumentInfoModal;